import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import * as firebase from 'firebase';
import 'firebase/performance';
import _Vue from 'vue';

import { IAuthGateway } from '@/core/interface/';
import {
  AppStartPresenterFunc,
} from '@/core/interface';
import { StartApp } from '@/core/usecase';

import {
  APIGateway,
  FileGateway,
  FirebaseAuthGateway,
} from '@/infrastructure';

import {
  AuthController,
  CategoryController,
  ClientController,
  ClientProductController,
  EntryController,
  JobController,
  OutletController,
  ProductController,
  ProjectControler,
  RetailerController,
  TaskController,
  UserController,
} from '@/delivery/controller';

export class CoreApp {
  public readonly auth: AuthController;
  public readonly category: CategoryController;
  public readonly client: ClientController;
  public readonly clientProduct: ClientProductController;
  public readonly entry: EntryController;
  public readonly job: JobController;
  public readonly outlet: OutletController;
  public readonly product: ProductController;
  public readonly project: ProjectControler;
  public readonly retailer: RetailerController;
  public readonly task: TaskController;
  public readonly user: UserController;

  private readonly apolloClient: ApolloClient<NormalizedCacheObject>;
  private readonly firebaseApp: firebase.app.App;
  private readonly perf: firebase.performance.Performance;

  private readonly authGateway: IAuthGateway;

  constructor(firebaseConfig: object, apolloClient: ApolloClient<NormalizedCacheObject>) {
    this.apolloClient = apolloClient;
    this.firebaseApp = firebase.initializeApp(firebaseConfig);

    // init Firebase Performance Monitoring
    this.perf = firebase.performance();

    this.authGateway = new FirebaseAuthGateway(this.firebaseApp.auth());
    const apiGateway = new APIGateway(this.apolloClient);
    const fileGateway = new FileGateway();

    this.auth = new AuthController(this.authGateway);
    this.category = new CategoryController(apiGateway);
    this.client = new ClientController(apiGateway);
    this.clientProduct = new ClientProductController(apiGateway);
    this.entry = new EntryController(apiGateway);
    this.job = new JobController(fileGateway, apiGateway);
    this.outlet = new OutletController(apiGateway);
    this.product = new ProductController(apiGateway);
    this.project = new ProjectControler(fileGateway, apiGateway, apiGateway);
    this.retailer = new RetailerController(fileGateway, apiGateway);
    this.task = new TaskController(apiGateway);
    this.user = new UserController(fileGateway, apiGateway);
  }

  public start(presenter: AppStartPresenterFunc) {
    const useCase = new StartApp(this.authGateway);

    useCase.execute(presenter);
  }
}

interface Options {
  app: CoreApp;
}

export function install(Vue: typeof _Vue, options?: Options): void {
  Vue.prototype.$coreApp = options!.app;
}
